import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAppSelector } from '@/store/hooks';
import {
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectUser,
  selectPermissions,
  selectAuthError,
  selectHasAnyPermission,
} from '@/store/slices/authSlice';
import { useAuthActions } from './AuthActionsContext';
import type { LoginOptions } from './authActions';
import { hasAnyPermission, hasAllPermissions } from './utils';
import type { NVXPermission } from './roles';

/**
 * Main auth hook - provides auth state and provider-agnostic methods
 */
export function useAuth() {
  const authActions = useAuthActions();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const user = useAppSelector(selectUser);
  const permissions = useAppSelector(selectPermissions);
  const error = useAppSelector(selectAuthError);

  const login = useCallback(
    async (options?: LoginOptions) => {
      try {
        await authActions.login(options);
      } catch (error) {
        console.error('Login failed:', error);
      }
    },
    [authActions],
  );

  const logout = useCallback(async () => {
    try {
      await authActions.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [authActions]);

  return {
    isAuthenticated,
    isLoading,
    user,
    permissions,
    error,
    login,
    logout,
  };
}

/**
 * Simple hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  return useAppSelector(selectIsAuthenticated);
}

/**
 * Hook to check if auth is still loading
 */
export function useIsAuthLoading(): boolean {
  return useAppSelector(selectIsAuthLoading);
}

/**
 * Hook that redirects to login if not authenticated
 * Use this in pages that require authentication
 */
export function useRequireAuth(redirectTo?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const { login } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        sessionStorage.setItem('returnUrl', pathname);
        login();
      }
    }
  }, [isAuthenticated, isLoading, navigate, pathname, redirectTo, login]);

  return { isAuthenticated, isLoading };
}

/**
 * Hook to check if user has ANY of the specified permissions
 */
export function useHasAnyPermission(
  requiredPermissions: NVXPermission[],
): boolean {
  const permissions = useAppSelector(selectPermissions);
  return hasAnyPermission(permissions, requiredPermissions);
}

/**
 * Hook to check if user has ALL of the specified permissions
 */
export function useHasAllPermissions(
  requiredPermissions: NVXPermission[],
): boolean {
  const permissions = useAppSelector(selectPermissions);
  return hasAllPermissions(permissions, requiredPermissions);
}

/**
 * Hook using selector for permission checking
 * Useful when you need to pass permissions dynamically
 */
export function useHasPermission(requiredPermissions: NVXPermission[]): boolean {
  return useAppSelector(selectHasAnyPermission(requiredPermissions));
}

/**
 * Hook that redirects to unauthorized page if user lacks required permissions
 */
export function useRequirePermission(
  requiredPermissions: NVXPermission[],
  unauthorizedPath = '/unauthorized',
) {
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const hasPermission = useHasAnyPermission(requiredPermissions);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasPermission) {
      navigate(unauthorizedPath);
    }
  }, [isAuthenticated, isLoading, hasPermission, navigate, unauthorizedPath]);

  return { hasPermission, isLoading };
}

